/**
 * <feature-stepper>
 * Auto-advancing vertical feature stepper with a synced media panel.
 * Each step can hold a Shopify-hosted video (advance on `ended`) or an image
 * (advance on a timed fallback). Pauses on hover and when scrolled out of view,
 * and respects prefers-reduced-motion.
 */
class FeatureStepper extends HTMLElement {
  constructor() {
    super();
    this.triggers = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.dots = Array.from(this.querySelectorAll('[data-fs-dot]'));
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.index = 0;
    this.paused = false;
    this.inView = true;
    this.rafId = null;
    this.timeStart = null;
    this._onEnded = null;
  }

  connectedCallback() {
    if (this.triggers.length === 0) return;

    this.triggers.forEach((trigger, i) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.select(i);
      });
      trigger.addEventListener('keydown', (e) => this.onKeyDown(e, i));
    });

    // Dots act as the slider control (primary navigation on mobile).
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.select(i));
    });

    // Pause on hover for mouse users only — touch taps fire pointerenter
    // without a matching pointerleave, which would freeze autoplay forever.
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') this.pause();
    });
    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') this.resume();
    });

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          if (entry.isIntersecting) this.resume();
          else this.pause();
        });
      },
      { threshold: 0.25 }
    );
    this.io.observe(this);

    if (this.reduceMotion) this.classList.add('fs--reduced-motion');

    this.activate(0);
    this.restartLoop();
  }

  disconnectedCallback() {
    this.stopLoop();
    if (this.io) this.io.disconnect();
  }

  currentVideo() {
    const panel = this.panels[this.index];
    return panel ? panel.querySelector('video[data-fs-video]') : null;
  }

  /** Switch to a step because the user asked for it (resets the timer). */
  select(index) {
    this.activate(index);
    this.restartLoop();
  }

  onKeyDown(event, index) {
    const count = this.triggers.length;
    let target = null;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.select(index);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      target = (index + 1) % count;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      target = (index - 1 + count) % count;
    } else if (event.key === 'Home') {
      target = 0;
    } else if (event.key === 'End') {
      target = count - 1;
    }

    if (target !== null) {
      event.preventDefault();
      this.select(target);
      this.triggers[target].focus();
    }
  }

  /** Apply the visual/active state for a step index. */
  activate(index) {
    const count = this.triggers.length;
    this.index = ((index % count) + count) % count;

    this.triggers.forEach((trigger, i) => {
      const isActive = i === this.index;
      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      trigger.setAttribute('tabindex', isActive ? '0' : '-1');
      const item = trigger.closest('[data-fs-item]');
      if (item) {
        item.classList.toggle('is-active', isActive);
        item.style.setProperty('--fs-progress', '0');
      }
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== this.index;
    });

    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));

    // Reset and play the active video; pause the rest.
    this.panels.forEach((panel, i) => {
      const video = panel.querySelector('video[data-fs-video]');
      if (!video) return;
      if (i === this.index) {
        try {
          video.currentTime = 0;
        } catch (e) {
          /* ignore */
        }
        this.playVideo(video);
      } else {
        video.pause();
      }
    });

    // Warm up the next step's video so advancing doesn't lag on buffering.
    const nextPanel = this.panels[(this.index + 1) % this.panels.length];
    const nextVideo = nextPanel && nextPanel.querySelector('video[data-fs-video]');
    if (nextVideo && nextVideo.preload !== 'auto') {
      nextVideo.preload = 'auto';
    }

    this.setProgress(0);
  }

  next() {
    this.activate(this.index + 1);
    this.restartLoop();
  }

  playVideo(video) {
    if (this.paused || this.reduceMotion) return;
    const promise = video.play();
    if (promise && promise.catch) promise.catch(() => {});
  }

  restartLoop() {
    this.stopLoop();
    if (!this.autoplay || this.reduceMotion || this.paused || !this.inView) return;

    this.timeStart = null;
    const video = this.currentVideo();
    if (video) {
      this._endedVideo = video;
      this._onEnded = () => this.next();
      video.addEventListener('ended', this._onEnded, { once: true });
    }
    this.loop();
  }

  loop() {
    const tick = (timestamp) => {
      if (this.paused || !this.inView) return;

      const video = this.currentVideo();
      let progress;

      if (video && video.duration) {
        progress = video.currentTime / video.duration;
        // `ended` listener handles advancing for videos.
      } else {
        if (this.timeStart === null) this.timeStart = timestamp;
        progress = (timestamp - this.timeStart) / this.interval;
        if (progress >= 1) {
          this.setProgress(1);
          this.next();
          return;
        }
      }

      this.setProgress(Math.min(progress, 1));
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stopLoop() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this._endedVideo && this._onEnded) {
      this._endedVideo.removeEventListener('ended', this._onEnded);
    }
    this._endedVideo = null;
    this._onEnded = null;
  }

  pause() {
    this.paused = true;
    this.stopLoop();
    const video = this.currentVideo();
    if (video) video.pause();
  }

  resume() {
    this.paused = false;
    const video = this.currentVideo();
    if (video) this.playVideo(video);
    this.restartLoop();
  }

  setProgress(value) {
    const activeTrigger = this.triggers[this.index];
    const item = activeTrigger ? activeTrigger.closest('[data-fs-item]') : null;
    if (item) item.style.setProperty('--fs-progress', String(value));
    const dot = this.dots[this.index];
    if (dot) dot.style.setProperty('--fs-progress', String(value));
  }
}

customElements.define('feature-stepper', FeatureStepper);
